import {
  diaryPaymentIndicator,
  type DiaryPaymentClassification,
  type DiaryPaymentIndicator,
  type DiaryPaymentMethod,
  type DiaryPaymentStatus,
} from "../booking/diaryPayments.js";

const LEGEND_NOW = new Date("2026-01-01T12:00:00.000Z");
const FUTURE_END = "2999-01-01T12:00:00.000Z";
const PAST_END = "2000-01-01T12:00:00.000Z";

type IndicatorFor = typeof diaryPaymentIndicator;

export interface DiaryPaymentKeyItem {
  symbol: DiaryPaymentIndicator["symbol"];
  label: string;
}

function classification(
  method: DiaryPaymentMethod,
  status: DiaryPaymentStatus,
): DiaryPaymentClassification {
  return {
    appointment_id: 0,
    method,
    status,
    payment_id: null,
    amount_due: null,
  };
}

function safeIndicator(
  indicatorFor: IndicatorFor,
  method: DiaryPaymentMethod,
  status: DiaryPaymentStatus,
  lessonEndsAt?: string,
  overrides: Partial<DiaryPaymentClassification> = {},
): DiaryPaymentIndicator | null {
  try {
    return indicatorFor({ ...classification(method, status), ...overrides }, lessonEndsAt, LEGEND_NOW);
  } catch {
    // The key is explanatory chrome, never a reason to make the Diary unusable.
    return null;
  }
}

function pushIndicator(
  items: DiaryPaymentKeyItem[],
  indicator: DiaryPaymentIndicator | null,
  label = indicator?.label,
): void {
  if (indicator === null || label === undefined) return;
  items.push({ symbol: indicator.symbol, label });
}

/** Build the legend lazily so classifier changes cannot blank the Diary route. */
export function buildDiaryPaymentKeyItems(
  indicatorFor: IndicatorFor = diaryPaymentIndicator,
): DiaryPaymentKeyItem[] {
  const items: DiaryPaymentKeyItem[] = [];

  pushIndicator(items, safeIndicator(indicatorFor, "unknown", "paid"));

  pushIndicator(items, safeIndicator(indicatorFor, "unknown", "paid", FUTURE_END, {
    payment_override: {
      id: "legend", booking_id: 0, reason: "Waived", amount_waived: "0.00",
      set_by_id: "legend", set_by_name: "Tutor", created_at: "2026-01-01",
    },
  }));

  const credit = safeIndicator(indicatorFor, "lesson_credit", "not_required");
  const coupon = safeIndicator(indicatorFor, "coupon", "paid");
  if (credit !== null && coupon !== null && credit.symbol === coupon.symbol) {
    // Both methods intentionally share `*`; avoid parsing punctuation from the
    // canonical paid label just to recover the method name.
    pushIndicator(items, credit, `${credit.label} / Coupon`);
  } else {
    pushIndicator(items, credit);
    pushIndicator(items, coupon);
  }

  const due = safeIndicator(indicatorFor, "on_site", "pending", FUTURE_END);
  const overdue = safeIndicator(indicatorFor, "on_site", "pending", PAST_END);
  if (due !== null && overdue !== null && due.symbol === overdue.symbol) {
    pushIndicator(items, due, `${due.label} / ${overdue.label}`);
  } else {
    pushIndicator(items, due);
    pushIndicator(items, overdue);
  }

  pushIndicator(items, safeIndicator(indicatorFor, "on_site", "partially_paid"));
  pushIndicator(items, safeIndicator(indicatorFor, "on_site", "failed"));

  const partRefunded = safeIndicator(indicatorFor, "on_site", "partially_refunded");
  const refunded = safeIndicator(indicatorFor, "on_site", "refunded");
  if (partRefunded !== null && refunded !== null && partRefunded.symbol === refunded.symbol) {
    pushIndicator(items, refunded, `${partRefunded.label} / ${refunded.label}`);
  } else {
    pushIndicator(items, partRefunded);
    pushIndicator(items, refunded);
  }

  const unknown = safeIndicator(indicatorFor, "unknown", "unknown");
  pushIndicator(items, unknown, unknown === null ? undefined : `${unknown.label} payment status`);

  return items;
}

export function DiaryPaymentKey() {
  const items = buildDiaryPaymentKeyItems();
  if (items.length === 0) return null;

  return (
    <details className="cal-payment-key">
      <summary>Payment key</summary>
      <div className="cal-payment-key-popover" role="group" aria-label="Diary payment symbols">
        <dl>
          {items.map((item) => (
            <div key={`${item.symbol}:${item.label}`} className="cal-payment-key-row">
              <dt aria-hidden="true">{item.symbol}</dt>
              <dd>{item.label}</dd>
            </div>
          ))}
        </dl>
        <p className="small muted">
          No symbol means no payment is required, or no live payment classification is available.
        </p>
      </div>
    </details>
  );
}
